<template>
  <header class="overview-header">
    <h1>Update Employee Information</h1>
  </header>

  <!-- Employee Selection -->
  <section class="form-section">
    <div class="form-group">
      <label for="employeeSelect">Select Employee to Update</label>
      <div class="employee-search-wrapper">
        <input
          v-model="employeeSearchTerm"
          type="text"
          placeholder="Search by name or employee ID..."
          @input="showEmployeeDropdown = employeeSearchTerm.length > 0"
          @focus="showEmployeeDropdown = employeeSearchTerm.length > 0"
          @blur="hideEmployeeDropdown"
        />
        <div v-if="showEmployeeDropdown" class="employee-dropdown">
          <div 
            v-for="emp in filteredEmployees" 
            :key="emp.id"
            class="employee-option"
            @mousedown="selectEmployee(emp)"
          >
            <div class="employee-name">{{ emp.firstName }} {{ emp.lastName }}</div>
            <div class="employee-details">ID: {{ emp.employeeId }} | {{ emp.position || 'No Position' }}</div>
          </div>
          <div v-if="filteredEmployees.length === 0" class="no-employees">
            No employees found
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Employee Update Form -->
  <section v-if="selectedEmployee" class="form-section">
    <h2>Update Information for {{ selectedEmployee.firstName }} {{ selectedEmployee.lastName }}</h2>

    <form @submit.prevent="updateEmployee">
      <!-- Personal Information -->
      <div class="form-subsection">
        <h3>Personal Information</h3>
        <div class="grid grid-3">
          <div class="form-group">
            <label for="firstName">First Name *</label>
            <input id="firstName" v-model="form.firstName" type="text" required />
          </div>
          <div class="form-group">
            <label for="middleName">Middle Name</label>
            <input id="middleName" v-model="form.middleName" type="text" />
          </div>
          <div class="form-group">
            <label for="lastName">Last Name *</label>
            <input id="lastName" v-model="form.lastName" type="text" required />
          </div>
        </div>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="dateOfBirth">Date of Birth</label>
            <input id="dateOfBirth" v-model="form.dateOfBirth" type="date" />
          </div>
          <div class="form-group">
            <label for="ssn">SSN</label>
            <div class="ssn-wrapper">
              <input id="ssn" v-model="form.ssn" v-mask="'###-##-####'" :type="showSSN ? 'text' : 'password'"
                autocomplete="off" placeholder="XXX-XX-XXXX" class="ssn-input" />
              <button type="button" class="ssn-toggle" @click="showSSN = !showSSN"
                aria-label="Toggle SSN visibility">
                {{ showSSN ? 'Hide' : 'Show' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Information -->
      <div class="form-subsection">
        <h3>Contact Information</h3>
        <div class="grid grid-2">
          <div class="form-group">
            <label for="email">Email *</label>
            <input id="email" v-model="form.email" type="email" required />
          </div>
          <div class="form-group">
            <label for="cellPhone">Cell Phone</label>
            <input id="cellPhone" v-model="form.cellPhone" type="tel" v-mask="'(###) ###-####'" />
          </div>
        </div>

        <div class="form-group">
          <label for="whatsApp">WhatsApp Number</label>
          <input id="whatsApp" v-model="form.whatsApp" type="tel" v-mask="'(###) ###-####'" />
        </div>
      </div>

      <!-- Address Information -->
      <div class="form-subsection">
        <h3>Local Address Information</h3>
        <div class="form-group">
          <label for="address">Street Address</label>
          <input id="address" v-model="form.address" type="text" />
        </div>

        <div class="grid grid-3">
          <div class="form-group">
            <label for="city">City</label>
            <input id="city" v-model="form.city" type="text" />
          </div>
          <div class="form-group">
            <label for="state">State</label>
            <input id="state" v-model="form.state" type="text" maxlength="2" placeholder="NC" />
          </div>
          <div class="form-group">
            <label for="zipCode">Zip Code</label>
            <input id="zipCode" v-model="form.zipCode" type="text" />
          </div>
        </div>

        <div class="form-group">
          <label for="country">Country</label>
          <input id="country" v-model="form.country" type="text" />
        </div>
      </div>

      <!-- US Address Information -->
      <div class="form-subsection">
        <h3>US Address Information</h3>
        <div class="form-group">
          <label for="usAddress">Street Address</label>
          <input id="usAddress" v-model="form.usAddress" type="text" />
        </div>

        <div class="grid grid-3">
          <div class="form-group">
            <label for="usCity">City</label>
            <input id="usCity" v-model="form.usCity" type="text" />
          </div>
          <div class="form-group">
            <label for="usState">State</label>
            <select id="usState" v-model="form.usState">
              <option value="">Select State...</option>
              <option value="AL">Alabama</option>
              <option value="AK">Alaska</option>
              <option value="AZ">Arizona</option>
              <option value="AR">Arkansas</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="CT">Connecticut</option>
              <option value="DE">Delaware</option>
              <option value="FL">Florida</option>
              <option value="GA">Georgia</option>
              <option value="HI">Hawaii</option>
              <option value="ID">Idaho</option>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="IA">Iowa</option>
              <option value="KS">Kansas</option>
              <option value="KY">Kentucky</option>
              <option value="LA">Louisiana</option>
              <option value="ME">Maine</option>
              <option value="MD">Maryland</option>
              <option value="MA">Massachusetts</option>
              <option value="MI">Michigan</option>
              <option value="MN">Minnesota</option>
              <option value="MS">Mississippi</option>
              <option value="MO">Missouri</option>
              <option value="MT">Montana</option>
              <option value="NE">Nebraska</option>
              <option value="NV">Nevada</option>
              <option value="NH">New Hampshire</option>
              <option value="NJ">New Jersey</option>
              <option value="NM">New Mexico</option>
              <option value="NY">New York</option>
              <option value="NC">North Carolina</option>
              <option value="ND">North Dakota</option>
              <option value="OH">Ohio</option>
              <option value="OK">Oklahoma</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
              <option value="RI">Rhode Island</option>
              <option value="SC">South Carolina</option>
              <option value="SD">South Dakota</option>
              <option value="TN">Tennessee</option>
              <option value="TX">Texas</option>
              <option value="UT">Utah</option>
              <option value="VT">Vermont</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="WV">West Virginia</option>
              <option value="WI">Wisconsin</option>
              <option value="WY">Wyoming</option>
              <option value="DC">District of Columbia</option>
            </select>
          </div>
          <div class="form-group">
            <label for="usZipCode">Zip Code</label>
            <input id="usZipCode" v-model="form.usZipCode" type="text" />
          </div>
        </div>

        <div class="form-group">
          <label for="usCountry">Country</label>
          <input id="usCountry" v-model="form.usCountry" type="text" value="United States" readonly class="readonly-field" />
        </div>
      </div>

      <!-- Employment Information -->
      <div class="form-subsection">
        <h3>Employment Information</h3>
        <div class="grid grid-2">
          <div class="form-group">
            <label for="employeeId">Employee ID</label>
            <input id="employeeId" v-model="form.employeeId" type="text" readonly class="readonly-field" />
          </div>
          <div class="form-group">
            <label for="position">Position</label>
            <input id="position" v-model="form.position" type="text" />
          </div>
        </div>

        <div class="grid grid-2">
          <div class="form-group">
            <label for="hireDate">Hire Date</label>
            <input id="hireDate" v-model="form.hireDate" type="date" />
          </div>
          <div class="form-group">
            <label for="status">Status</label>
            <select id="status" v-model="form.status">
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Terminated">Terminated</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="companySelected">Company</label>
          <input id="companySelected" v-model="form.companySelected" type="text" />
        </div>
      </div>

      <!-- Additional Companies -->
      <div v-if="selectedEmployee" class="form-subsection">
        <h3 style="color: #1e293b; font-weight: 700; margin-bottom: 16px; font-size: 18px;">Additional Companies</h3>
        <p style="color: #64748b; font-size: 14px; margin-bottom: 12px;">
          Primary Company: <strong>{{ form.companySelected || 'Not specified' }}</strong>
        </p>
        <p style="color: #64748b; font-size: 14px; margin-bottom: 16px;">
          Select additional companies this employee should be associated with:
        </p>
        
        <div class="additional-companies-container" style="background: #f8fafc; border-radius: 12px; padding: 16px; border: 1px solid #e2e8f0;">
          <div class="company-list">
            <div v-for="company in companies" :key="company.id" class="company-item-with-selection">
              <div class="company-checkbox-section">
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    :value="company.id"
                    @change="toggleCompanyAssignment(company.id, $event.target.checked)"
                    :checked="isCompanyAssigned(company.id)"
                  />
                  <div class="company-info">
                    <div class="company-name">{{ company.companyName }}</div>
                    <div class="company-details" v-if="company.address">{{ company.address }}</div>
                  </div>
                </label>
              </div>
            </div>
            
            <div v-if="companies.length === 0" class="no-companies">
              <span style="color: #64748b; font-style: italic;">No additional companies available</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Rent Information -->
      <div class="form-subsection">
        <h3>Rent Information</h3>
        <div class="grid grid-2">
          <div class="form-group">
            <label for="rentAmount">Rent Amount ($)</label>
            <input id="rentAmount" v-model.number="form.rentAmount" type="number" min="0" step="0.01" placeholder="0.00" />
          </div>
          <div class="form-group">
            <label for="rentCycle">Rent Cycle</label>
            <select id="rentCycle" v-model="form.rentCycle">
              <option value="">Select Cycle...</option>
              <option value="weekly">Weekly</option>
              <option value="2-week">Every 2 Weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Property Assignment -->
      <div class="form-subsection">
        <h3 style="color: #1e293b; font-weight: 700; margin-bottom: 16px; font-size: 18px;">Property Assignment</h3>
        <div class="form-group">
          <label for="assignedProperties" style="color: #475569; font-weight: 600; font-size: 14px; margin-bottom: 12px; display: block;">Assigned Properties with Hourly Rates</label>
          <div class="property-assignment" style="background: #f8fafc; border-radius: 12px; padding: 16px; border: 1px solid #e2e8f0;">
            <div class="property-list">
              <div v-for="property in properties" :key="property.id" class="property-item-with-rate">
                <div class="property-checkbox-section">
                  <label class="checkbox-label">
                    <input 
                      type="checkbox" 
                      :value="property.id"
                      @change="togglePropertyAssignment(property.id, $event.target.checked)"
                      :checked="isPropertyAssigned(property.id)"
                    />
                    <div class="property-info">
                      <div class="property-name">{{ property.propertyName }}</div>
                      <div class="property-address">{{ property.address || property.billTo }}</div>
                    </div>
                  </label>
                </div>
                <div v-if="isPropertyAssigned(property.id)" class="property-rate-section">
                  <label class="rate-label">Hourly Rate</label>
                  <div style="position: relative;">
                    <span style="position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #64748b; font-weight: 500; z-index: 10; pointer-events: none;">$</span>
                    <input 
                      type="number" 
                      :value="getPropertyRate(property.id)"
                      @input="updatePropertyRate(property.id, $event.target.value)"
                      min="0" 
                      step="0.01" 
                      placeholder="15.00"
                      class="rate-input"
                      style="padding-left: 24px;"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Emergency Contact -->
      <div class="form-subsection">
        <h3>Emergency Contact</h3>
        <div class="grid grid-2">
          <div class="form-group">
            <label for="emergencyContactName">Emergency Contact Name</label>
            <input id="emergencyContactName" v-model="form.emergencyContactName" type="text" />
          </div>
          <div class="form-group">
            <label for="emergencyContactPhone">Emergency Contact Phone</label>
            <input id="emergencyContactPhone" v-model="form.emergencyContactPhone" type="tel" v-mask="'(###) ###-####'" />
          </div>
        </div>
      </div>

      <!-- Passport Information -->
      <div class="form-subsection">
        <h3>Passport Information</h3>
        <div class="grid grid-2">
          <div class="form-group">
            <label for="passportNumber">Passport Number</label>
            <input id="passportNumber" v-model="form.passportNumber" type="text" />
          </div>
          <div class="form-group">
            <label for="passportExpirationDate">Passport Expiration Date</label>
            <input id="passportExpirationDate" v-model="form.passportExpirationDate" type="date" />
          </div>
        </div>

        <div class="form-group">
          <label for="passportImage">Passport Image</label>
          <div class="passport-image-container">
            <div v-if="form.passportImageUrl" class="passport-image-box">
              <img 
                :src="form.passportImageUrl" 
                alt="Passport Image" 
                class="passport-image"
                @error="handleImageError"
              />
              <div class="image-overlay">
                <a :href="form.passportImageUrl" target="_blank" class="view-full-size">
                  View Full Size
                </a>
              </div>
            </div>
            <div v-else class="no-passport-image">
              <div class="no-image-icon">📄</div>
              <p>No passport image available</p>
            </div>
          </div>
          <small class="form-help">Passport images can only be updated through the application process</small>
        </div>
      </div>

      <!-- Document URLs (Read-only) -->
      <div class="form-subsection">
        <h3>Documents (Read-only)</h3>
        <div class="grid grid-2">
          <div class="form-group">
            <label for="resumeUrl">Resume URL</label>
            <input id="resumeUrl" v-model="form.resumeUrl" type="url" readonly class="readonly-field" />
            <a v-if="form.resumeUrl" :href="form.resumeUrl" target="_blank" class="document-link">View Resume</a>
          </div>
          <div class="form-group">
            <label for="signatureUrl">Signature URL</label>
            <input id="signatureUrl" v-model="form.signatureUrl" type="url" readonly class="readonly-field" />
            <a v-if="form.signatureUrl" :href="form.signatureUrl" target="_blank" class="document-link">View Signature</a>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button type="submit" class="save-button" :disabled="!selectedEmployee">
          Update Employee Information
        </button>
        <button type="button" class="delete-button" @click="deleteEmployee">
          Delete Employee
        </button>
        <button type="button" class="cancel-button" @click="clearSelection">
          Close Form
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { onAuthStateChanged } from 'firebase/auth'
import {
  collection, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore'
import { db, auth } from '@/firebase'
import Swal from 'sweetalert2'

/** ---------- Reactive state ---------- */
const router = useRouter()
const employees = ref([])
const properties = ref([])
const companies = ref([])
const selectedEmployee = ref(null)
const employeeSearchTerm = ref('')
const showEmployeeDropdown = ref(false)
const showSSN = ref(false)

let unsubEmployees = null
let unsubProperties = null
let unsubCompanies = null

const form = reactive({
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  cellPhone: '',
  whatsApp: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  usAddress: '',
  usCity: '',
  usState: '',
  usZipCode: '',
  usCountry: 'United States',
  employeeId: '',
  position: '',
  hireDate: '',
  status: 'Active',
  companySelected: '',
  additionalCompanies: [],
  rentAmount: 0,
  rentCycle: '',
  assignedProperties: [],
  emergencyContactName: '',
  emergencyContactPhone: '',
  passportNumber: '',
  passportExpirationDate: '',
  passportImageUrl: '',
  resumeUrl: '',
  signatureUrl: '',
  ssn: ''
})

/** ---------- Computed ---------- */
const filteredEmployees = computed(() => {
  if (!employeeSearchTerm.value || employeeSearchTerm.value.length < 1) return []
  
  const term = employeeSearchTerm.value.toLowerCase()
  return employees.value.filter(emp => {
    const firstName = (emp.firstName || '').toLowerCase()
    const lastName = (emp.lastName || '').toLowerCase()
    const employeeId = (emp.employeeId || '').toLowerCase()
    const fullName = `${firstName} ${lastName}`.trim()
    
    return firstName.includes(term) || 
           lastName.includes(term) || 
           fullName.includes(term) ||
           employeeId.includes(term)
  }).slice(0, 10)
})

/** ---------- Lifecycle ---------- */
onMounted(() => {
  const un = onAuthStateChanged(auth, (user) => {
    if (!user) {
      router.replace({ name: 'Login' })
    } else {
      // Load employees
      unsubEmployees = onSnapshot(collection(db, 'employees'), (snap) => {
        employees.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      }, (error) => {
        console.error('Error loading employees:', error)
      })

      // Load properties
      unsubProperties = onSnapshot(collection(db, 'properties'), (snap) => {
        properties.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      }, (error) => {
        console.error('Error loading properties:', error)
      })

      // Load companies
      unsubCompanies = onSnapshot(collection(db, 'companies'), (snap) => {
        companies.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      }, (error) => {
        console.error('Error loading companies:', error)
      })
    }
    un()
  })
})

onUnmounted(() => {
  if (unsubEmployees) unsubEmployees()
  if (unsubProperties) unsubProperties()
  if (unsubCompanies) unsubCompanies()
})

/** ---------- Handlers ---------- */
function selectEmployee(employee) {
  selectedEmployee.value = employee
  employeeSearchTerm.value = `${employee.firstName} ${employee.lastName}`
  showEmployeeDropdown.value = false
  
  // Populate form with employee data
  form.firstName = employee.firstName || ''
  form.middleName = employee.middleName || ''
  form.lastName = employee.lastName || ''
  form.dateOfBirth = employee.dateOfBirth || ''
  form.email = employee.email || ''
  form.cellPhone = employee.cellPhone || ''
  form.whatsApp = employee.whatsApp || ''
  form.address = employee.address || ''
  form.city = employee.city || ''
  form.state = employee.state || ''
  form.zipCode = employee.zipCode || ''
  form.country = employee.country || ''
  form.usAddress = employee.usAddress || ''
  form.usCity = employee.usCity || ''
  form.usState = employee.usState || ''
  form.usZipCode = employee.usZipCode || ''
  form.usCountry = employee.usCountry || 'United States'
  form.employeeId = employee.employeeId || ''
  form.position = employee.position || ''
  form.hireDate = employee.hireDate || ''
  form.status = employee.status || 'Active'
  form.companySelected = employee.companySelected || ''
  form.additionalCompanies = employee.additionalCompanies || []
  form.rentAmount = employee.rentAmount || 0
  form.rentCycle = employee.rentCycle || ''
  form.assignedProperties = employee.assignedProperties || []
  form.emergencyContactName = employee.emergencyContactName || ''
  form.emergencyContactPhone = employee.emergencyContactPhone || ''
  form.passportNumber = employee.passportNumber || ''
  form.passportExpirationDate = employee.passportExpirationDate || ''
  form.passportImageUrl = employee.passportImageUrl || ''
  form.resumeUrl = employee.resumeUrl || ''
  form.signatureUrl = employee.signatureUrl || ''
  form.ssn = employee.ssn || ''
}

function isPropertyAssigned(propertyId) {
  return form.assignedProperties.some(assignment => 
    typeof assignment === 'string' ? assignment === propertyId : assignment.propertyId === propertyId
  )
}

function getPropertyRate(propertyId) {
  const assignment = form.assignedProperties.find(assignment => 
    typeof assignment === 'string' ? assignment === propertyId : assignment.propertyId === propertyId
  )
  
  if (typeof assignment === 'object' && assignment.hourlyRate !== undefined) {
    return assignment.hourlyRate
  }
  return 0
}

function togglePropertyAssignment(propertyId, isChecked) {
  if (isChecked) {
    // Add property if not already assigned
    if (!isPropertyAssigned(propertyId)) {
      // Remove any old string-based assignment
      form.assignedProperties = form.assignedProperties.filter(assignment => 
        typeof assignment === 'string' ? assignment !== propertyId : assignment.propertyId !== propertyId
      )
      // Add new object-based assignment
      form.assignedProperties.push({
        propertyId: propertyId,
        hourlyRate: 0
      })
    }
  } else {
    // Remove property assignment
    form.assignedProperties = form.assignedProperties.filter(assignment => 
      typeof assignment === 'string' ? assignment !== propertyId : assignment.propertyId !== propertyId
    )
  }
}

function updatePropertyRate(propertyId, rate) {
  const assignmentIndex = form.assignedProperties.findIndex(assignment => 
    typeof assignment === 'string' ? assignment === propertyId : assignment.propertyId === propertyId
  )
  
  if (assignmentIndex !== -1) {
    const assignment = form.assignedProperties[assignmentIndex]
    if (typeof assignment === 'string') {
      // Convert string-based assignment to object-based
      form.assignedProperties[assignmentIndex] = {
        propertyId: propertyId,
        hourlyRate: Number(rate) || 0
      }
    } else {
      // Update existing object-based assignment
      form.assignedProperties[assignmentIndex].hourlyRate = Number(rate) || 0
    }
  }
}

// Company assignment helper functions
function isCompanyAssigned(companyId) {
  return form.additionalCompanies.includes(companyId)
}

function toggleCompanyAssignment(companyId, isChecked) {
  if (isChecked) {
    if (!form.additionalCompanies.includes(companyId)) {
      form.additionalCompanies.push(companyId)
    }
  } else {
    const index = form.additionalCompanies.indexOf(companyId)
    if (index !== -1) {
      form.additionalCompanies.splice(index, 1)
    }
  }
}

function hideEmployeeDropdown() {
  setTimeout(() => {
    showEmployeeDropdown.value = false
  }, 200)
}

function handleImageError(event) {
  // Hide the broken image and show a placeholder
  event.target.style.display = 'none'
  const container = event.target.closest('.passport-image-box')
  if (container) {
    container.innerHTML = `
      <div class="image-error">
        <div class="error-icon">⚠️</div>
        <p>Unable to load image</p>
      </div>
    `
  }
}

function clearSelection() {
  selectedEmployee.value = null
  employeeSearchTerm.value = ''
  showEmployeeDropdown.value = false
  
  // Reset form
  Object.keys(form).forEach(key => {
    if (key === 'status') {
      form[key] = 'Active'
    } else if (key === 'assignedProperties') {
      form[key] = []
    } else if (key === 'usCountry') {
      form[key] = 'United States'
    } else if (key === 'rentAmount') {
      form[key] = 0
    } else {
      form[key] = ''
    }
  })
}

async function updateEmployee() {
  if (!selectedEmployee.value) {
    Swal.fire({
      icon: 'warning',
      title: 'No Employee Selected',
      text: 'Please select an employee to update.',
      confirmButtonColor: '#2563eb'
    })
    return
  }

  // Validation
  if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
    Swal.fire({
      icon: 'error',
      title: 'Missing Required Fields',
      text: 'Please fill in all required fields (First Name, Last Name, Email).',
      confirmButtonColor: '#2563eb'
    })
    return
  }

  try {
    const updateData = {
      firstName: form.firstName.trim(),
      middleName: form.middleName.trim(),
      lastName: form.lastName.trim(),
      dateOfBirth: form.dateOfBirth,
      email: form.email.trim().toLowerCase(),
      cellPhone: form.cellPhone,
      whatsApp: form.whatsApp,
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      zipCode: form.zipCode.trim(),
      country: form.country.trim(),
      usAddress: form.usAddress.trim(),
      usCity: form.usCity.trim(),
      usState: form.usState.trim(),
      usZipCode: form.usZipCode.trim(),
      usCountry: form.usCountry.trim(),
      position: form.position.trim(),
      hireDate: form.hireDate,
      status: form.status,
      companySelected: form.companySelected.trim(),
      additionalCompanies: form.additionalCompanies,
      rentAmount: Number(form.rentAmount) || 0,
      rentCycle: form.rentCycle,
      assignedProperties: form.assignedProperties,
      emergencyContactName: form.emergencyContactName.trim(),
      emergencyContactPhone: form.emergencyContactPhone,
      passportNumber: form.passportNumber.trim(),
      passportExpirationDate: form.passportExpirationDate,
      ssn: form.ssn.trim(),
      updatedAt: serverTimestamp()
    }

    // Only update non-document fields (passport image, resume, signature URLs are read-only)
    await updateDoc(doc(db, 'employees', selectedEmployee.value.id), updateData)
    
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: 'Employee information updated successfully!',
      confirmButtonColor: '#10b981',
      timer: 3000,
      timerProgressBar: true
    })
    
  } catch (error) {
    console.error('Error updating employee:', error)
    Swal.fire({
      icon: 'error',
      title: 'Update Failed',
      text: 'Error updating employee information. Please try again.',
      confirmButtonColor: '#ef4444'
    })
  }
}

async function deleteEmployee() {
  if (!selectedEmployee.value) return

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Delete Employee',
    html: `<p>Are you sure you want to delete <strong>${selectedEmployee.value.firstName} ${selectedEmployee.value.lastName}</strong>?</p><p style="color: #dc2626; font-size: 12px;">This action cannot be undone. All employee records will be permanently deleted.</p>`,
    showCancelButton: true,
    confirmButtonText: 'Yes, Delete Employee',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626',
  })

  if (!result.isConfirmed) return

  Swal.fire({
    title: 'Deleting...',
    html: '<div class="custom-loader"></div>',
    allowOutsideClick: false,
    showConfirmButton: false,
  })

  try {
    await deleteDoc(doc(db, 'employees', selectedEmployee.value.id))

    await Swal.fire({
      icon: 'success',
      title: 'Deleted',
      text: `${selectedEmployee.value.firstName} ${selectedEmployee.value.lastName} has been permanently deleted.`,
      confirmButtonColor: '#10b981',
      timer: 2000,
      timerProgressBar: true
    })

    clearSelection()
  } catch (error) {
    console.error('Error deleting employee:', error)
    Swal.fire({
      icon: 'error',
      title: 'Delete Failed',
      text: 'Error deleting employee. Please try again.',
      confirmButtonColor: '#ef4444'
    })
  }
}
</script>

<style scoped>
.ssn-wrapper {
  position: relative;
}

.ssn-wrapper .ssn-input {
  width: 100%;
  padding-right: 3.5rem;
}

.ssn-toggle {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  cursor: pointer;
  border-radius: 0.25rem;
}

.ssn-toggle:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Reuse existing styles */
.overview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.overview-header h1 {
  font-size: 24px;
  color: #1f2937;
}

.form-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,.1);
}

.form-section h2 {
  margin-bottom: 16px;
  font-size: 20px;
  color: #111827;
}

.form-subsection {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.form-subsection:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.form-subsection h3 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #374151;
  font-weight: 600;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.form-group label {
  margin-bottom: 4px;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.form-group input,
.form-group select {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
}

.readonly-field {
  background: #f3f4f6 !important;
  color: #6b7280;
  cursor: not-allowed;
}

.form-help {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.document-link {
  color: #2563eb;
  text-decoration: none;
  font-size: 12px;
  margin-top: 4px;
  display: inline-block;
}

.document-link:hover {
  text-decoration: underline;
}

.grid {
  display: grid;
  gap: 12px;
}

.grid-2 {
  grid-template-columns: 1fr 1fr;
}

.grid-3 {
  grid-template-columns: 1fr 1fr 1fr;
}

@media (max-width: 820px) {
  .grid-2, .grid-3 {
    grid-template-columns: 1fr;
  }
}

/* Employee Search Styles */
.employee-search-wrapper {
  position: relative;
}

.employee-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.employee-option {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.employee-option:hover {
  background-color: #f3f4f6;
}

.employee-option:last-child {
  border-bottom: none;
}

.employee-name {
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.employee-details {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.no-employees {
  padding: 8px 12px;
  color: #6b7280;
  font-style: italic;
  font-size: 12px;
  text-align: center;
}

/* Property Assignment Styles */
.property-assignment {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.property-list {
  padding: 8px;
}

.property-item {
  margin-bottom: 8px;
}

.property-item:last-child {
  margin-bottom: 0;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  font-size: 14px;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 8px;
  margin-top: 2px;
}

.property-name {
  font-weight: 500;
  color: #374151;
  margin-right: 8px;
}

.property-address {
  color: #6b7280;
  font-size: 12px;
}

/* Property Assignment with Rates Styles - Modern Design */
.property-item-with-rate {
  margin-bottom: 16px;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease-in-out;
  overflow: hidden;
}

.property-item-with-rate:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-1px);
}

.property-item-with-rate:last-child {
  margin-bottom: 0;
}

.property-checkbox-section {
  padding: 16px;
  margin-bottom: 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  position: relative;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 12px;
  margin-top: 0;
  width: 18px;
  height: 18px;
  accent-color: #3b82f6;
  cursor: pointer;
}

.property-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.property-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 15px;
  margin-bottom: 4px;
}

.property-address {
  color: #64748b;
  font-size: 13px;
  opacity: 0.8;
}

.property-rate-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-top: 1px solid #e2e8f0;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.rate-label {
  font-size: 13px;
  color: #475569;
  font-weight: 600;
  letter-spacing: 0.025em;
}

.rate-input {
  padding: 8px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  width: 120px;
  background: #ffffff;
  transition: all 0.2s ease-in-out;
  color: #1e293b;
}

.rate-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background: #fefefe;
}

.rate-input:hover {
  border-color: #94a3b8;
}

/* Enhanced property assignment container */
.property-assignment {
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.property-assignment::-webkit-scrollbar {
  width: 6px;
}

.property-assignment::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.property-assignment::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.property-assignment::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Empty state styling */
.property-list:empty::before {
  content: "No properties available";
  display: block;
  text-align: center;
  color: #64748b;
  font-style: italic;
  padding: 32px;
  background: #f8fafc;
  border-radius: 8px;
  border: 2px dashed #cbd5e1;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
}

.save-button {
  background: #10b981;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.save-button:hover {
  background: #059669;
}

.save-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.delete-button {
  background: #ef4444;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.delete-button:hover {
  background: #dc2626;
}

.cancel-button {
  background: #6b7280;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-button:hover {
  background: #4b5563;
}

/* Passport Image Styles */
.passport-image-container {
  margin-top: 8px;
}

.passport-image-box {
  position: relative;
  width: 200px;
  height: 250px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.passport-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}

.passport-image:hover {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.passport-image-box:hover .image-overlay {
  opacity: 1;
}

.view-full-size {
  color: white;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
}

.view-full-size:hover {
  text-decoration: underline;
}

.no-passport-image {
  width: 200px;
  height: 250px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  color: #6b7280;
}

.no-image-icon {
  font-size: 48px;
  margin-bottom: 8px;
}

.no-passport-image p {
  margin: 0;
  font-size: 14px;
  text-align: center;
}

.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ef4444;
}

.error-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.image-error p {
  margin: 0;
  font-size: 12px;
  text-align: center;
}

/* Additional Companies Section Styles */
.additional-companies-container {
  max-height: 300px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.additional-companies-container::-webkit-scrollbar {
  width: 6px;
}

.additional-companies-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.additional-companies-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.additional-companies-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.company-item-with-selection {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease-in-out;
}

.company-item-with-selection:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
}

.company-item-with-selection:last-child {
  margin-bottom: 0;
}

.company-checkbox-section {
  margin-bottom: 0;
}

.company-checkbox-section .checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
}

.company-checkbox-section input[type="checkbox"] {
  margin-right: 12px;
  margin-top: 0;
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
}

.company-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.company-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 15px;
  margin-bottom: 2px;
}

.company-details {
  color: #64748b;
  font-size: 13px;
  opacity: 0.8;
}

.no-companies {
  text-align: center;
  padding: 24px;
  background: #f8fafc;
  border-radius: 8px;
  border: 2px dashed #cbd5e1;
}
</style>